import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GEO_URL = '/istanbul-districts.json';

// Renk skalası: yeşil → sarı → turuncu → kırmızı
const COLOR_SCALE = [
  { t: 0, r: 34, g: 197, b: 94 },    // yeşil
  { t: 0.33, r: 234, g: 179, b: 8 },  // sarı
  { t: 0.66, r: 249, g: 115, b: 22 }, // turuncu
  { t: 1, r: 239, g: 68, b: 68 },     // kırmızı
];

const interpolateColor = (ratio) => {
  if (ratio <= 0) return COLOR_SCALE[0];
  if (ratio >= 1) return COLOR_SCALE[COLOR_SCALE.length - 1];
  for (let i = 0; i < COLOR_SCALE.length - 1; i++) {
    const a = COLOR_SCALE[i], b = COLOR_SCALE[i + 1];
    if (ratio >= a.t && ratio <= b.t) {
      const local = (ratio - a.t) / (b.t - a.t);
      return {
        r: Math.round(a.r + (b.r - a.r) * local),
        g: Math.round(a.g + (b.g - a.g) * local),
        b: Math.round(a.b + (b.b - a.b) * local),
      };
    }
  }
  return COLOR_SCALE[COLOR_SCALE.length - 1];
};

const IstanbulHaritaPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [emlaklar, setEmlaklar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hoveredDistrict, setHoveredDistrict] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [filterType, setFilterType] = useState('all'); // 'all', 'arsa', 'yapi'

  useEffect(() => {
    if (!user?.is_admin && !user?.has_emlak_kamulastirma_access) {
      navigate('/dashboard');
      return;
    }
    fetchEmlaklar();
  }, []);

  const fetchEmlaklar = async () => {
    try {
      const response = await axios.get(`${API}/emlak-islemleri`, { withCredentials: true });
      setEmlaklar(response.data);
    } catch (error) {
      console.error('Veri hatasi:', error);
    } finally {
      setLoading(false);
    }
  };

  const normalize = (name) => (name || '').toUpperCase().trim();

  // Filtrelenmiş emlak listesi
  const filteredEmlaklar = useMemo(() => {
    if (filterType === 'all') return emlaklar;
    return emlaklar.filter(e => e.tasinmaz_turu === filterType);
  }, [emlaklar, filterType]);

  // İlçe bazında taşınmaz sayısı (filtreye göre)
  const districtCounts = useMemo(() => {
    const counts = {};
    filteredEmlaklar.forEach(e => {
      const ilce = normalize(e.ilce);
      if (ilce) counts[ilce] = (counts[ilce] || 0) + 1;
    });
    return counts;
  }, [filteredEmlaklar]);

  // İlçe bazında toplam alan (filtreye göre)
  const districtAreas = useMemo(() => {
    const areas = {};
    filteredEmlaklar.forEach(e => {
      const ilce = normalize(e.ilce);
      const alan = parseFloat(String(e.alan_m2 || '0').replace(/[^0-9.,]/g, '').replace(',', '.'));
      if (ilce) areas[ilce] = (areas[ilce] || 0) + (isNaN(alan) ? 0 : alan);
    });
    return areas;
  }, [filteredEmlaklar]);

  const maxCount = Math.max(1, ...Object.values(districtCounts));

  // Renk hesapla
  const getColor = (name) => {
    const key = normalize(name);
    const count = districtCounts[key] || 0;
    if (count === 0) return '#1e293b';
    const ratio = Math.min(count / maxCount, 1);
    const c = interpolateColor(ratio);
    return `rgb(${c.r}, ${c.g}, ${c.b})`;
  };

  // Sınır rengi
  const getStroke = (name) => {
    const key = normalize(name);
    const count = districtCounts[key] || 0;
    if (count === 0) return '#334155';
    const ratio = Math.min(count / maxCount, 1);
    const c = interpolateColor(ratio);
    return `rgba(${c.r}, ${c.g}, ${c.b}, 0.7)`;
  };

  const handleClick = (geo) => {
    const name = geo.properties.name;
    const key = normalize(name);
    const items = filteredEmlaklar.filter(e => normalize(e.ilce) === key);
    const totalArea = items.reduce((sum, e) => {
      const a = parseFloat(String(e.alan_m2 || '0').replace(/[^0-9.,]/g, '').replace(',', '.'));
      return sum + (isNaN(a) ? 0 : a);
    }, 0);
    setSelectedDistrict({ name, items, totalArea });
    setDialogOpen(true);
  };

  const formatArea = (val) => {
    if (!val && val !== 0) return '-';
    return parseFloat(val).toLocaleString('tr-TR');
  };

  const getMulkiyetText = (m) => {
    const map = { 'milli_emlak': 'Milli Emlak', 'ogm': 'OGM', 'belediye': 'Belediye', 'il_mudurlugu': 'İl Müd.', 'kamu_kurum': 'Kamu Kurum', 'diger': 'Diğer' };
    return map[m] || m || '-';
  };

  const filterLabel = filterType === 'arsa' ? 'Arsa' : filterType === 'yapi' ? 'Yapı' : 'Tümü';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Üst Menü */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Emlak Harita Görünümü</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">Anasayfa</Button>
              <Button onClick={() => navigate('/emlak-kamulastirma')} variant="ghost" className="text-gray-300 hover:text-white">Geri Dön</Button>
              <Button onClick={() => navigate('/emlak-kamulastirma/emlak-islemleri')} variant="ghost" className="text-blue-400 hover:text-blue-300">Tablo Görünümü</Button>
              <Button onClick={async () => { await logout(); navigate('/login'); }} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-5">
        {/* Başlık + Filtre + Lejant */}
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">İstanbul Tesis Envanteri Haritası</h2>
              <p className="text-gray-400 text-sm">
                {filteredEmlaklar.length} taşınmaz gösteriliyor
                {filterType !== 'all' && ` (${filterLabel})`}
                {' | İlçeye tıklayarak detay görün'}
              </p>
            </div>
          </div>

          {/* Filtre ve Lejant */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Tür Filtresi */}
            <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800 rounded-lg px-3 py-2" data-testid="filter-bar">
              <span className="text-xs text-gray-400 mr-1">Filtre:</span>
              <Button
                data-testid="filter-all"
                size="sm"
                onClick={() => setFilterType('all')}
                className={`h-7 px-3 text-xs ${filterType === 'all' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              >
                Tümü ({emlaklar.length})
              </Button>
              <Button
                data-testid="filter-arsa"
                size="sm"
                onClick={() => setFilterType('arsa')}
                className={`h-7 px-3 text-xs ${filterType === 'arsa' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              >
                Arsa ({emlaklar.filter(e => e.tasinmaz_turu === 'arsa').length})
              </Button>
              <Button
                data-testid="filter-yapi"
                size="sm"
                onClick={() => setFilterType('yapi')}
                className={`h-7 px-3 text-xs ${filterType === 'yapi' ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              >
                Yapı ({emlaklar.filter(e => e.tasinmaz_turu === 'yapi').length})
              </Button>
            </div>

            {/* Renk Skalası Lejantı */}
            <div className="flex items-center gap-3 bg-gray-900/60 border border-gray-800 rounded-lg px-4 py-2" data-testid="legend">
              <span className="text-xs text-gray-400">Yoğunluk:</span>
              <div className="flex items-center gap-1">
                <div className="w-4 h-3 rounded" style={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}></div>
                <span className="text-xs text-gray-500">0</span>
              </div>
              <div className="flex items-center">
                <div className="h-3 rounded-sm" style={{
                  width: '120px',
                  background: 'linear-gradient(to right, rgb(34,197,94), rgb(234,179,8), rgb(249,115,22), rgb(239,68,68))'
                }}></div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span>Az</span>
                <span>Orta</span>
                <span className="text-red-400 font-semibold">Çok</span>
              </div>
            </div>
          </div>
        </div>

        {/* Harita */}
        <Card className="bg-gray-900 border-gray-800 overflow-hidden relative" data-testid="istanbul-map-card">
          <CardContent className="p-2">
            <div style={{ width: '100%', height: '500px' }}
              onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
            >
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 38000, center: [29.01, 41.04] }}
                width={900}
                height={480}
                style={{ width: '100%', height: '100%' }}
              >
                <ZoomableGroup zoom={1} minZoom={0.8} maxZoom={4}>
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const name = geo.properties.name;
                        const key = normalize(name);
                        const count = districtCounts[key] || 0;
                        const isHovered = hoveredDistrict === name;
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={isHovered ? '#60a5fa' : getColor(name)}
                            stroke={isHovered ? '#93c5fd' : getStroke(name)}
                            strokeWidth={isHovered ? 1.8 : 0.5}
                            onMouseEnter={() => setHoveredDistrict(name)}
                            onMouseLeave={() => setHoveredDistrict(null)}
                            onClick={() => handleClick(geo)}
                            style={{
                              default: { outline: 'none', cursor: 'pointer', transition: 'fill 0.2s' },
                              hover: { outline: 'none', cursor: 'pointer' },
                              pressed: { outline: 'none' }
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
            </div>

            {/* Fare üstü bilgi kutusu */}
            {hoveredDistrict && (
              <div
                className="fixed bg-gray-800/95 border border-gray-600 rounded-lg px-4 py-2 shadow-xl pointer-events-none z-50"
                style={{ left: tooltipPos.x + 16, top: tooltipPos.y - 10 }}
              >
                <p className="text-white font-bold text-sm">{hoveredDistrict}</p>
                <p className="text-gray-300 text-xs">
                  {districtCounts[normalize(hoveredDistrict)] || 0} taşınmaz
                  {filterType !== 'all' && ` (${filterLabel})`}
                  {districtAreas[normalize(hoveredDistrict)] ? ` | ${formatArea(districtAreas[normalize(hoveredDistrict)])} m\u00B2` : ''}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Özet Kartları */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs mb-1">Toplam Taşınmaz {filterType !== 'all' && `(${filterLabel})`}</p>
              <p className="text-2xl font-bold text-white" data-testid="total-count">{filteredEmlaklar.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs mb-1">Kayıtlı İlçe</p>
              <p className="text-2xl font-bold text-blue-400" data-testid="district-count">{Object.keys(districtCounts).length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs mb-1">Toplam Alan</p>
              <p className="text-2xl font-bold text-green-400" data-testid="total-area">{formatArea(Object.values(districtAreas).reduce((a, b) => a + b, 0))} m²</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs mb-1">Arsa / Yapı</p>
              <p className="text-2xl font-bold text-purple-400" data-testid="type-counts">
                {emlaklar.filter(e => e.tasinmaz_turu === 'arsa').length} / {emlaklar.filter(e => e.tasinmaz_turu === 'yapi').length}
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* İlçe Detay Diyalogu */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" data-testid="district-dialog-title">
              {selectedDistrict?.name} - Taşınmaz Envanteri
              {filterType !== 'all' && ` (${filterLabel})`}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedDistrict?.items.length || 0} kayıt bulundu
            </DialogDescription>
          </DialogHeader>

          {selectedDistrict && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs">Taşınmaz</p>
                  <p className="text-xl font-bold text-white">{selectedDistrict.items.length}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs">Toplam Alan</p>
                  <p className="text-xl font-bold text-green-400">{formatArea(selectedDistrict.totalArea)} m²</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs">Arsa / Yapı</p>
                  <p className="text-xl font-bold text-blue-400">
                    {selectedDistrict.items.filter(e => e.tasinmaz_turu === 'arsa').length} / {selectedDistrict.items.filter(e => e.tasinmaz_turu === 'yapi').length}
                  </p>
                </div>
              </div>

              {selectedDistrict.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="district-detail-table">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-300 text-xs">S.NO</th>
                        <th className="px-3 py-2 text-left text-gray-300 text-xs">Tür</th>
                        <th className="px-3 py-2 text-left text-gray-300 text-xs">Mahalle</th>
                        <th className="px-3 py-2 text-left text-gray-300 text-xs">Ada/Parsel</th>
                        <th className="px-3 py-2 text-left text-gray-300 text-xs">Mülkiyet</th>
                        <th className="px-3 py-2 text-left text-gray-300 text-xs">Alan (m²)</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      {selectedDistrict.items.map((e, idx) => (
                        <tr key={e.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                          <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${e.tasinmaz_turu === 'arsa' ? 'bg-green-900/50 text-green-300' : 'bg-blue-900/50 text-blue-300'}`}>
                              {e.tasinmaz_turu === 'arsa' ? 'Arsa' : 'Yapı'}
                            </span>
                          </td>
                          <td className="px-3 py-2">{e.mahalle || '-'}</td>
                          <td className="px-3 py-2 text-xs">{e.ada || '-'} / {e.parsel || '-'}</td>
                          <td className="px-3 py-2 text-xs">{getMulkiyetText(e.mulkiyet)}</td>
                          <td className="px-3 py-2 font-medium">{formatArea(e.alan_m2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">Bu ilçede kayıtlı taşınmaz bulunmamaktadır.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IstanbulHaritaPage;
